import logger, { toErrorMessage, type LogContext } from './logger'
import { appIsOutdated } from './versionClient'

/**
 * Whether a refused request is news.
 *
 * Every non-2xx used to be logged at error level, which in this app means a
 * Sentry issue. So a wrong password raised one, and so did every expired
 * session — the two most ordinary events an authenticated app has. An error
 * tracker that reports the system working is one people learn to scroll past,
 * and then it cannot tell them when something is actually wrong.
 *
 * The question is not "was this request refused" but "did the server do the
 * right thing with what it was sent".
 *
 * - **401** — the credentials were wrong, or the session is over. Both are
 *   answers, not faults, and the app has a designed response to each: retry the
 *   refresh, or sign out. It stays a breadcrumb, so it is still there in the
 *   context of a real error without being one.
 * - **409** — a conflict we raise on purpose, like a lane already booked. Being
 *   told is the feature.
 * - **403 and 404 stay errors.** A screen that offers an action the API forbids,
 *   or links somewhere that is not there, is a bug nearly every time — three
 *   were found that way in one evening, and downgrading them would have hidden
 *   every one.
 * - **A request that never got an answer is not a refusal.** It has no status
 *   for this function to judge; `logRequestFailure` records it as a warning.
 * - **Everything else stays an error**, 5xx included.
 */
export function isExpectedRefusal(status: number): boolean {
  return status === 401 || status === 409
}

/**
 * The status an API failure carried, if it carried one.
 *
 * Read structurally rather than by importing `isApiError`, because the client
 * imports this module: going the other way would make a cycle out of two files
 * that only need one fact from each other.
 */
function statusOf<E>(error: E): number | null {
  if (typeof error !== 'object' || error === null) return null
  if (!('status' in error)) return null
  const { status } = error
  return typeof status === 'number' ? status : null
}

/**
 * Report a request that failed, at the level it deserves.
 *
 * This is the line that becomes the issue, because it is the one that says what
 * the person was trying to do — "Failed to save event phase" rather than a path
 * and a number. The client logs the path and status just before, as a
 * breadcrumb, so the issue arrives with the transport detail already attached.
 * The two used to be errors both, which filed the same failure twice under the
 * less useful title.
 *
 * Nothing is filed for a refusal the server was right to give — see
 * `isExpectedRefusal`. A wrong password is not a fault of the app's.
 */
export function logRequestFailure<E>(message: string, context: LogContext, error: E): void {
  const status = statusOf(error)
  const detail: LogContext = {
    ...context,
    error: toErrorMessage(error),
    // Whether anyone could see the page. A visible tab no longer decides the
    // level — a dropped request looks the same either way — but the breadcrumb
    // still says which it was.
    ...(status === 0 && typeof document !== 'undefined' ? { visibility: document.visibilityState } : {}),
  }
  if (status !== null && isExpectedRefusal(status)) {
    logger.warn(message, detail)
    return
  }
  // A refusal the server composed for a person to read, rather than a complaint
  // about the request itself. Nothing is broken: they need a fresh invitation,
  // or their actual password.
  if (status === 400 && isRefusalForAPerson(error)) {
    logger.warn(message, detail)
    return
  }
  // No HTTP response at all. `status: 0` is what the client throws when fetch
  // rejects: Chrome's "Failed to fetch" (which the error tracker rewrites to
  // name the host), Firefox's "NetworkError", Safari's "Load failed". A radio
  // that blinked, a laptop that slept mid-request, a privacy tool that ate it.
  //
  // A visible page, and a browser that claims to be online, do not make this
  // an outage. Both were true of the reports this quiets — one person, one
  // event, and the API never answered. An outage that does answer still falls
  // through: a 502 from the edge, a 500 from us. So does an exception that is
  // not a failed request, which carries no status. This stays a breadcrumb,
  // with whether the page was visible, so the next real error still has it.
  if (status === 0) {
    logger.warn(message, detail)
    return
  }
  // The same failure the client just decided about: an out-of-date tab cannot
  // read a newer server's replies, and is already being told to reload.
  if (isStaleShapeFailure(error)) {
    logger.warn(message, detail)
    return
  }
  // A limit on something a person typed — too long, too short — rather than a
  // payload this client built wrongly. The screen is meant to say so and keep
  // the text. Filing it taught Sentry to call a validation problem an outage:
  // a description over 200 characters became "Failed to add a timeline entry".
  // A bad type or an unknown key is a different code and stays an error, which
  // is how a wire-format change announces itself. Codes the server did not
  // send stay an error too; unexplained is not the same as harmless.
  if (status === 400 && isConstraintOnWhatWasTyped(error)) {
    logger.warn(message, detail)
    return
  }
  logger.error(message, detail)
}

/**
 * A 400 the client brought on itself: a payload that failed the API's schema.
 *
 * `ZodValidationPipe` is the only thing in the API that attaches `details`, so
 * its presence separates the two kinds of 400. One means this client built a
 * request wrongly — a real bug, and how a wire-format change would announce
 * itself — except a constraint on what was typed, which
 * `isConstraintOnWhatWasTyped` keeps quiet. The other is a refusal composed for
 * a person to read: an expired invitation, a wrong current password. Being told
 * is the feature there.
 */
function isSchemaRejection<E>(error: E): boolean {
  return hasBodyKey(error, 'details')
}

/**
 * A 400 the server composed for a person: a sentence, and no complaint about the
 * shape of the request. An expired invitation, a wrong current password.
 *
 * The sentence is required rather than assumed. A 400 carrying neither — from a
 * proxy, or something that is not our API at all — is unexplained, and
 * unexplained is not the same as harmless.
 */
function isRefusalForAPerson<E>(error: E): boolean {
  return hasBodyKey(error, 'message') && !isSchemaRejection(error)
}

/** Whether an `ApiError`'s parsed body carries this key, read structurally. */
function hasBodyKey<E>(error: E, key: string): boolean {
  if (typeof error !== 'object' || error === null || !('body' in error)) return false
  const { body } = error
  return typeof body === 'object' && body !== null && key in body
}

/**
 * Zod codes for a constraint on a value the person can edit and send again.
 *
 * Anything else (`invalid_type`, `unrecognized_keys`, `invalid_format`) means
 * this client built a payload the API could not read.
 */
const TYPED_CONSTRAINT = new Set(['too_big', 'too_small'])

/**
 * Whether a schema rejection is only limits on what was typed.
 *
 * `issueCodes` is attached by `ZodValidationPipe` and nothing else. Absent, or
 * mixed with a code that is not a constraint, this is not that case — a client
 * that cannot tell stays loud.
 */
function isConstraintOnWhatWasTyped<E>(error: E): boolean {
  const codes = issueCodesOf(error)
  return codes !== null && codes.every((code) => TYPED_CONSTRAINT.has(code))
}

/** The Zod issue codes on an `ApiError` body, read structurally. */
function issueCodesOf<E>(error: E): string[] | null {
  if (typeof error !== 'object' || error === null || !('body' in error)) return null
  const { body } = error
  if (typeof body !== 'object' || body === null || !('details' in body)) return null
  const { details } = body
  if (typeof details !== 'object' || details === null || !('issueCodes' in details)) return null
  const { issueCodes } = details
  if (!Array.isArray(issueCodes) || issueCodes.length === 0) return null
  const codes: string[] = []
  for (const code of issueCodes) {
    if (typeof code !== 'string') return null
    codes.push(code)
  }
  return codes
}

/** A schema failure in a tab that already knows it is running older JS. */
function isStaleShapeFailure<E>(error: E): boolean {
  return error instanceof Error && error.name === 'ResponseShapeError' && appIsOutdated()
}
